import type { WidgetConfig, PostVoteBehavior, PostVotePathConfig } from '../types';

/**
 * Normalize a legacy WidgetConfig into the new PostVoteBehavior[] pipeline.
 *
 * Handles three legacy shapes:
 *  1. Very old: postVoteMode / positiveEmailCapture / etc.
 *  2. Intermediate: PostVotePathConfig objects (collectFeedback, collectEmail, …)
 *  3. Current: PostVoteBehavior[] arrays — returned as-is.
 *
 * Safe to call on already-migrated configs (no-op).
 */
export function migrateWidgetConfig(config: WidgetConfig): WidgetConfig {
  // Already in new array format → no-op
  if (Array.isArray(config.positivePostVote) || Array.isArray(config.negativePostVote)) {
    return config;
  }

  // Check for intermediate PostVotePathConfig objects (non-array truthy value)
  const posObj = config.positivePostVote as unknown as PostVotePathConfig | undefined;
  const negObj = config.negativePostVote as unknown as PostVotePathConfig | undefined;

  if (posObj && typeof posObj === 'object') {
    return migrateFromPathConfig(config, posObj, negObj);
  }

  // Oldest legacy: postVoteMode, positiveEmailCapture, etc.
  return migrateFromLegacy(config);
}

/* ── Migrate from PostVotePathConfig objects ─────────────────────────── */

function pathConfigToBehaviors(
  obj: PostVotePathConfig | undefined,
  prefix: 'positive' | 'negative',
  updates: Partial<WidgetConfig>
): PostVoteBehavior[] {
  if (!obj) return ['auto-dismiss'];

  const behaviors: PostVoteBehavior[] = [];

  if (obj.collectFeedback) {
    behaviors.push('detailed-feedback');
    const key =
      prefix === 'positive' ? 'positiveFeedbackPlaceholder' : 'negativeFeedbackPlaceholder';
    updates[key] = obj.feedbackPlaceholder || 'Tell us more...';
  }

  if (obj.collectEmail) {
    behaviors.push('collect-email');
    const key = prefix === 'positive' ? 'positiveEmailHeadline' : 'negativeEmailHeadline';
    updates[key] =
      obj.emailHeadline || (prefix === 'positive' ? "We'll notify you!" : 'Help us improve');
  }

  if (obj.thankYouMessage) {
    behaviors.push('custom-message');
    const key = prefix === 'positive' ? 'positiveCustomMessage' : 'negativeCustomMessage';
    updates[key] = obj.thankYouMessage;
  }

  return behaviors.length > 0 ? behaviors : ['auto-dismiss'];
}

function migrateFromPathConfig(
  config: WidgetConfig,
  posObj: PostVotePathConfig | undefined,
  negObj: PostVotePathConfig | undefined
): WidgetConfig {
  const updates: Partial<WidgetConfig> = {};
  const positivePostVote = pathConfigToBehaviors(posObj, 'positive', updates);
  const negativePostVote = pathConfigToBehaviors(negObj, 'negative', updates);

  return { ...config, ...updates, positivePostVote, negativePostVote };
}

/* ── Migrate from oldest legacy fields ───────────────────────────────── */

function migrateFromLegacy(config: WidgetConfig): WidgetConfig {
  const posBehaviors: PostVoteBehavior[] = [];
  const negBehaviors: PostVoteBehavior[] = [];
  const updates: Partial<WidgetConfig> = {};

  const posMode = config.positivePostVoteMode ?? config.postVoteMode ?? 'dismiss';
  const negMode = config.negativePostVoteMode ?? config.postVoteMode ?? 'dismiss';

  // "input" mode → detailed-feedback
  if (posMode === 'input') {
    posBehaviors.push('detailed-feedback');
    updates.positiveFeedbackPlaceholder = config.placeholder || 'Tell us more...';
  }
  if (negMode === 'input') {
    negBehaviors.push('detailed-feedback');
    updates.negativeFeedbackPlaceholder = config.placeholder || 'Tell us more...';
  }

  // "message" mode → custom-message
  if (posMode === 'message') {
    posBehaviors.push('custom-message');
    updates.positiveCustomMessage = config.positiveMessage || config.thankYouMessage;
  }
  if (negMode === 'message') {
    negBehaviors.push('custom-message');
    updates.negativeCustomMessage = config.negativeMessage || config.thankYouMessage;
  }

  // Legacy email capture
  if (config.positiveEmailCapture) {
    posBehaviors.push('collect-email');
    updates.positiveEmailHeadline =
      config.positiveHeadline || config.emailPlaceholder || "We'll notify you!";
  }
  if (config.negativeEmailCapture) {
    negBehaviors.push('collect-email');
    updates.negativeEmailHeadline = config.negativeHeadline || 'Help us improve';
  }

  // Legacy `captureEmail` without path-specific flags → both paths
  if (config.captureEmail && !config.positiveEmailCapture && !config.negativeEmailCapture) {
    if (!posBehaviors.includes('collect-email')) {
      posBehaviors.push('collect-email');
      updates.positiveEmailHeadline = config.emailPlaceholder || "We'd love to follow up!";
    }
    if (!negBehaviors.includes('collect-email')) {
      negBehaviors.push('collect-email');
      updates.negativeEmailHeadline = config.emailPlaceholder || "We'd love to follow up!";
    }
  }

  // Carry over thankYouMessage for dismiss mode as custom-message
  if (posMode === 'dismiss' && config.thankYouMessage) {
    posBehaviors.push('custom-message');
    updates.positiveCustomMessage = config.thankYouMessage;
  }
  if (negMode === 'dismiss' && config.thankYouMessage) {
    negBehaviors.push('custom-message');
    updates.negativeCustomMessage = config.thankYouMessage;
  }

  // Default to auto-dismiss if nothing configured
  if (posBehaviors.length === 0) posBehaviors.push('auto-dismiss');
  if (negBehaviors.length === 0) negBehaviors.push('auto-dismiss');

  return {
    ...config,
    ...updates,
    positivePostVote: posBehaviors,
    negativePostVote: negBehaviors,
  };
}
